/*
  Memories Made Outside product page behaviour. No dependencies, loaded with defer
  by snippets/mmo-product-summary.liquid.

  1. Delivery estimate: turns "4–6 working days" into dates in the shopper's own
     timezone (the page may be cached, so this can't be done in Liquid). Weekends
     and the merchant's skip dates (bank holidays) don't count. No JS keeps the
     working-days sentence. Framed prints and the digital download change it.
     The price under the title follows the size and frame chosen in the customiser.
  2. The preview: a compact copy pinned to the top on phones once the full-size
     preview scrolls away, and an "Enlarge" view (tap to zoom) on all screens,
     for the example print and the photos too ([data-mmo-zoom]). Sends
     preview_enlarged (location: preview, mini, example, print, photo or size_guide).
  3. Photos: thumbnails under the image that swap a photo into the preview's
     place, arrows on the image, and a sideways swipe on phones. The image on
     show stays until the next one has loaded. Sends pdp_photo_view (position: 2
     for the first photo, as "Your design" is 1).
  4. Click tracking for data-mmo-event elements, as on the homepage (mmo-home.js):
     only the location is sent, through the web pixel's allowlist.
  5. customiser_step: the first time the customer changes or taps something in
     each of the customiser's steps (step: 1 route, 2 details, 3 design, 4 size
     and frame), once per step per page. Every product counts the same way,
     whether its steps come one at a time or all on one page. Here, not in the
     customiser, which is at its size budget.
*/
(() => {
  // ---- Delivery estimate ----------------------------------------------
  const isoDay = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const addWorkingDays = (from, days, skip) => {
    const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    let left = days;
    while (left > 0) {
      d.setDate(d.getDate() + 1);
      const weekday = d.getDay();
      if (weekday !== 0 && weekday !== 6 && !skip.has(isoDay(d))) left -= 1;
    }
    return d;
  };

  const formatRange = (start, end) => {
    const day = { weekday: 'short', day: 'numeric' };
    const full = { weekday: 'short', day: 'numeric', month: 'short' };
    const sameMonth = start.getMonth() === end.getMonth();
    const first = start.toLocaleDateString('en-GB', sameMonth ? day : full);
    return `${first} – ${end.toLocaleDateString('en-GB', full)}`;
  };

  // The same promise sits in the basket row, right above Add to basket: the size
  // step's bar, or the page-level row on ready-made routes.
  let barNote = '';
  const placeBarNote = () => {
    if (!barNote) return;
    document.querySelectorAll('[data-mmo-product] [data-step="size"] .mmo-c__bar, [data-mmo-product] .mmo-c__bar[data-step="basket"]').forEach((bar) => {
      let note = bar.querySelector('.mmo-bar-note');
      if (!note) {
        note = document.createElement('p');
        note.className = 'mmo-bar-note';
        bar.querySelector('[data-bar-price]')?.after(note);
      }
      note.textContent = barNote;
    });
  };
  const setBarNote = (text) => {
    barNote = text;
    placeBarNote();
  };

  // The box follows the format chosen in the customiser ([data-mmo-format] on its
  // root): unframed and framed prints have their own ranges, and the digital
  // download says how the file arrives instead, with the gift line hidden.
  const deliveries = [...document.querySelectorAll('[data-mmo-delivery]')].map((el) => {
    const title = el.querySelector('[data-mmo-delivery-title]');
    const target = el.querySelector('[data-mmo-delivery-estimate]');
    const gift = el.closest('.mmo-summary')?.querySelector('[data-mmo-gift]');
    const printTitle = title ? title.textContent : '';
    const skip = new Set((el.dataset.skip || '').match(/\d{4}-\d{2}-\d{2}/g) || []);
    return (format) => {
      if (!title || !target) return;
      if (format === 'digital' && el.dataset.digitalTitle) {
        title.textContent = el.dataset.digitalTitle;
        target.textContent = el.dataset.digitalText || '';
        if (gift) gift.hidden = true;
        setBarNote(el.dataset.digitalTitle);
        return;
      }
      title.textContent = printTitle;
      if (gift) gift.hidden = false;
      const min = Number(el.dataset.min);
      const framedMax = Number(el.dataset.maxFramed);
      const max = format === 'framed' && Number.isInteger(framedMax) && framedMax >= min ? framedMax : Number(el.dataset.max);
      if (!Number.isInteger(min) || !Number.isInteger(max) || min < 1 || max < min) return;
      const today = new Date();
      const range = formatRange(addWorkingDays(today, min, skip), addWorkingDays(today, max, skip));
      // The dates stand out; the condition sits under them.
      const dates = document.createElement('strong');
      dates.className = 'mmo-summary__delivery-dates';
      dates.textContent = `Arrives ${range}`;
      target.replaceChildren(dates);
      setBarNote(`${printTitle} · arrives ${range}`);
    };
  });
  const showFormat = (format) => deliveries.forEach((show) => show(format));
  showFormat('');
  const page = document.querySelector('[data-mmo-product]');
  if (page && deliveries.length) {
    let current = '';
    const sync = () => {
      const format = page.querySelector('[data-mmo-format]')?.dataset.mmoFormat || '';
      if (format === current) return;
      current = format;
      showFormat(format);
    };
    sync();
    new MutationObserver(sync).observe(page, { subtree: true, attributes: true, attributeFilter: ['data-mmo-format'] });
  }

  // ---- Price under the title follows the selection ----------------------
  // The customiser writes the chosen size, frame and price into its basket row
  // ([data-bar-price]). The price at the top shows that price, so the two never
  // disagree, and keeps "From" while it's still the starting price.
  const summaryPrice = document.querySelector('[data-mmo-product] .mmo-summary__price');
  const product = document.querySelector('[data-mmo-product]');
  if (summaryPrice && product) {
    const amount = summaryPrice.querySelector('.mmo-data');
    const from = summaryPrice.querySelector('.mmo-summary__from');
    const start = amount ? amount.textContent.trim() : '';
    const follow = (bar) => {
      placeBarNote();
      const sync = () => {
        const chosen = bar.querySelector('.mmo-c__bar-amount');
        if (!chosen || !amount) return;
        amount.textContent = chosen.textContent;
        if (from) from.hidden = chosen.textContent.trim() !== start;
      };
      sync();
      new MutationObserver(sync).observe(bar, { childList: true, subtree: true, characterData: true });
    };
    const existing = product.querySelector('[data-bar-price]');
    if (existing) follow(existing);
    else {
      const wait = new MutationObserver(() => {
        const bar = product.querySelector('[data-bar-price]');
        if (!bar) return;
        wait.disconnect();
        follow(bar);
      });
      wait.observe(product, { childList: true, subtree: true });
    }
  }

  // ---- Preview: compact copy on phones, and Enlarge ---------------------
  // The customiser moves its preview into the product block's image column
  // ([data-mmo-preview-slot]). On phones it's full size at the top of the page;
  // once it scrolls away, a compact copy pins to the top of the screen so each
  // change stays in view while the options scroll under it. "Enlarge" opens the
  // print full screen (tap to zoom, scroll to pan), on phones and desktop. The
  // example print and the photos have their own, which opens a larger copy of the image.
  const slot = document.querySelector('[data-mmo-product] [data-mmo-preview-slot]');
  if (slot) {
    const phone = window.matchMedia('(max-width: 989px)');
    const ICON =
      '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20" width="16" height="16"><path d="M8.5 3a5.5 5.5 0 1 0 3.4 9.8l3.6 3.7 1.1-1.1-3.7-3.6A5.5 5.5 0 0 0 8.5 3Zm0 1.6a3.9 3.9 0 1 1 0 7.8 3.9 3.9 0 0 1 0-7.8ZM7.7 6v1.7H6v1.6h1.7V11h1.6V9.3H11V7.7H9.3V6H7.7Z" fill="currentColor"/></svg>';

    const enlargeButton = (location) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'mmo-enlarge';
      b.dataset.mmoEnlarge = location;
      b.innerHTML = `${ICON}<span>Enlarge</span>`;
      return b;
    };

    // A copy of the current print (frame and art) for the compact bar and the enlarged view.
    const copyOf = (frame) => {
      const c = frame.cloneNode(true);
      c.querySelectorAll('.mmo-enlarge, .mmo-c__sample-badge, figcaption').forEach((e) => e.remove());
      c.removeAttribute('aria-labelledby');
      c.removeAttribute('id');
      c.querySelectorAll('[id]').forEach((e) => e.removeAttribute('id'));
      return c;
    };

    // Compact bar (phones).
    const mini = document.createElement('div');
    mini.className = 'mmo-c mmo-mini';
    mini.innerHTML = '<div class="mmo-mini__art" aria-hidden="true"></div><p class="mmo-mini__line" aria-hidden="true"></p>';
    mini.appendChild(enlargeButton('mini'));
    const miniLine = mini.querySelector('.mmo-mini__line');
    // The title, date and time typed so far: the small preview is too small to read them.
    const detailsLine = () => {
      const value = (name) => document.querySelector(`[data-mmo-customiser] [data-field="${name}"]`)?.value.trim() || '';
      const iso = value('date');
      const date = /^\d{4}-\d{2}-\d{2}$/.test(iso)
        ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : '';
      return [value('title'), date, value('finishTime')].filter(Boolean).join(' · ');
    };
    document.body.appendChild(mini);
    const miniArt = mini.querySelector('.mmo-mini__art');

    // Enlarged view.
    const zoom = document.createElement('dialog');
    zoom.className = 'mmo-zoom';
    zoom.setAttribute('aria-label', 'Your print, enlarged');
    zoom.innerHTML =
      '<div class="mmo-zoom__bar"><p class="mmo-zoom__hint"></p>' +
      '<button type="button" class="mmo-zoom__close" aria-label="Close">×</button></div>' +
      '<div class="mmo-zoom__stage"><div class="mmo-c mmo-zoom__art"></div></div>';
    document.body.appendChild(zoom);
    const zoomArt = zoom.querySelector('.mmo-zoom__art');
    const stage = zoom.querySelector('.mmo-zoom__stage');
    const hint = zoom.querySelector('.mmo-zoom__hint');

    let preview = null;
    let frame = null;
    let shown = false;
    let pending = 0;

    const syncMini = () => {
      if (frame) miniArt.replaceChildren(copyOf(frame));
      miniLine.textContent = detailsLine();
    };

    const update = () => {
      if (!preview || !frame) return;
      const product = slot.closest('[data-mmo-product]');
      const show =
        phone.matches &&
        !preview.hasAttribute('data-empty') &&
        frame.getBoundingClientRect().bottom < 0 &&
        product.getBoundingClientRect().bottom > window.innerHeight * 0.4;
      if (show === shown) return;
      shown = show;
      if (show) syncMini();
      mini.classList.toggle('is-shown', show);
    };

    const onScroll = () => {
      if (pending) return;
      pending = requestAnimationFrame(() => {
        pending = 0;
        update();
      });
    };

    let noun = 'print';
    const setZoomed = (on, x = 0.5, y = 0.5) => {
      zoom.classList.toggle('is-zoomed', on);
      hint.textContent = on ? `Tap again to see the whole ${noun}` : `Tap the ${noun} to zoom in`;
      if (on) {
        requestAnimationFrame(() => {
          stage.scrollLeft = x * stage.scrollWidth - stage.clientWidth / 2;
          stage.scrollTop = y * stage.scrollHeight - stage.clientHeight / 2;
        });
      }
    };

    let pushed = false;
    const LABELS = { example: 'The example print, enlarged', print: 'The print, enlarged', photo: 'Photo, enlarged', size_guide: 'Size guide, enlarged' };

    // The example print or a photo: what's on the page straight away, then the larger copy once it has loaded.
    const zoomImage = (holder) => {
      const shown = holder.querySelector('img');
      if (!shown) return null;
      const img = document.createElement('img');
      img.className = 'mmo-zoom__img';
      img.alt = shown.alt;
      img.src = shown.currentSrc || shown.src;
      const w = Number(shown.getAttribute('width'));
      const h = Number(shown.getAttribute('height'));
      if (w && h) img.style.setProperty('--mmo-zoom-ar', String(w / h));
      img.style.setProperty('--mmo-zoom-px', `${w || 1200}px`);
      const large = new Image();
      large.onload = () => {
        img.src = large.src;
        img.style.setProperty('--mmo-zoom-px', `${large.naturalWidth}px`);
      };
      large.src = holder.dataset.mmoZoom;
      return img;
    };

    const openZoom = (location, holder) => {
      const art = holder ? zoomImage(holder) : frame && copyOf(frame);
      if (!art) return;
      zoomArt.replaceChildren(art);
      noun = !holder || location === 'example' || location === 'print' ? 'print' : 'image';
      zoom.setAttribute('aria-label', LABELS[location] || 'Your print, enlarged');
      setZoomed(false);
      zoom.showModal();
      // The back gesture closes the enlarged view rather than leaving the page.
      history.pushState({ mmoZoom: true }, '');
      pushed = true;
      try {
        window.Shopify?.analytics?.publish?.('preview_enlarged', { location });
      } catch {
        // Analytics must never get in the way.
      }
    };

    const closeZoom = () => {
      if (zoom.open) zoom.close();
    };

    zoom.addEventListener('close', () => {
      if (pushed) {
        pushed = false;
        history.back();
      }
    });
    window.addEventListener('popstate', () => {
      if (zoom.open) {
        pushed = false;
        zoom.close();
      }
    });
    zoom.querySelector('.mmo-zoom__close').addEventListener('click', closeZoom);
    zoom.addEventListener('click', (event) => {
      if (event.target === zoom) {
        closeZoom();
        return;
      }
      const art = event.target instanceof Element ? event.target.closest('.mmo-zoom__art .mmo-c__frame, .mmo-zoom__img') : null;
      if (!art) return;
      const r = art.getBoundingClientRect();
      setZoomed(!zoom.classList.contains('is-zoomed'), (event.clientX - r.left) / r.width, (event.clientY - r.top) / r.height);
    });

    document.addEventListener('click', (event) => {
      const b = event.target instanceof Element ? event.target.closest('[data-mmo-enlarge]') : null;
      if (b) openZoom(b.dataset.mmoEnlarge, b.closest('[data-mmo-zoom]'));
    });
    slot.querySelectorAll('[data-mmo-zoom]').forEach((el) => el.appendChild(enlargeButton(el.dataset.mmoZoomLocation)));

    // The customiser moves the preview into the slot once it has mounted.
    const attach = () => {
      const p = slot.querySelector(':scope > .mmo-c__preview');
      const f = p && p.querySelector('.mmo-c__frame');
      if (!f || f === frame) return false;
      preview = p;
      frame = f;
      if (!frame.querySelector('.mmo-enlarge')) frame.appendChild(enlargeButton('preview'));
      // Keep the compact copy current while it's on screen.
      let t = 0;
      new MutationObserver(() => {
        if (!shown) return;
        clearTimeout(t);
        t = setTimeout(syncMini, 150);
      }).observe(frame, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-frame', 'data-orientation'] });
      new MutationObserver(update).observe(preview, { attributes: true, attributeFilter: ['data-empty'] });
      update();
      return true;
    };

    if (!attach()) {
      const watch = new MutationObserver(() => {
        if (attach()) watch.disconnect();
      });
      watch.observe(slot, { childList: true });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  // ---- Photos: thumbnails after "Your design" --------------------------
  // A photo shows in the preview's place; "Your design", or changing any option,
  // brings the preview back so each change is seen.
  const thumbs = slot ? [...slot.querySelectorAll('[data-mmo-thumb]')] : [];
  if (thumbs.length) {
    const imageOf = (button) => {
      const id = button && button.dataset.mmoThumb;
      const photo = id && document.getElementById(id);
      return photo ? photo.querySelector('img') : null;
    };

    // The image on show stays until the chosen one has loaded (or failed, or 3 seconds have gone), so the stage is never blank.
    let choosing = 0;
    const show = (button) => {
      const id = button.dataset.mmoThumb;
      thumbs.forEach((t) => t.setAttribute('aria-pressed', String(t === button)));
      const token = ++choosing;
      const swap = () => {
        if (token !== choosing) return;
        choosing += 1;
        slot.querySelectorAll('[data-mmo-photo]').forEach((p) => {
          p.hidden = p.id !== id;
        });
        if (id) slot.dataset.mmoView = 'photo';
        else delete slot.dataset.mmoView;
        // Fetch the one after, so Next shows it straight away.
        const next = imageOf(thumbs[(thumbs.indexOf(button) + 1) % thumbs.length]);
        if (next) next.loading = 'eager';
      };
      const img = imageOf(button);
      if (!img || img.complete) return swap();
      img.loading = 'eager';
      img.addEventListener('load', swap, { once: true });
      img.addEventListener('error', swap, { once: true });
      setTimeout(swap, 3000);
    };

    thumbs.forEach((t) =>
      t.addEventListener('click', () => {
        show(t);
        // On phones the thumbnails sit under the image: keep the top of it in view.
        const shown = slot.querySelector('[data-mmo-photo]:not([hidden])') || slot.querySelector(':scope > .mmo-c__preview');
        if (shown && shown.getBoundingClientRect().top < 0) shown.scrollIntoView({ block: 'start' });
        if (!t.dataset.mmoThumb) return;
        try {
          window.Shopify?.analytics?.publish?.('pdp_photo_view', { position: Number(t.dataset.mmoPosition) });
        } catch {
          // Analytics must never get in the way.
        }
      }),
    );

    // The next or previous image, as a thumbnail tap would (so it sends pdp_photo_view too).
    const step = (dir) => {
      const at = thumbs.findIndex((t) => t.getAttribute('aria-pressed') === 'true');
      const next = thumbs[(at + dir + thumbs.length) % thumbs.length];
      next.click();
      // Bring its thumbnail into the strip's view without scrolling the page.
      const strip = next.closest('.mmo-product__thumbs');
      const s = strip.getBoundingClientRect();
      const n = next.getBoundingClientRect();
      strip.scrollBy({ left: n.left - s.left - (s.width - n.width) / 2 });
    };
    slot.querySelectorAll('[data-mmo-step]').forEach((b) => b.addEventListener('click', () => step(Number(b.dataset.mmoStep))));

    // Phones: swipe the image sideways for the next or previous one. Not on the
    // preview while the Design step is open, where dragging moves the map.
    let start = null;
    slot.addEventListener(
      'touchstart',
      (event) => {
        const t = event.target;
        const panning = t instanceof Element && t.closest('.mmo-c__preview') && slot.dataset.activeStep === 'design';
        const controls = t instanceof Element && t.closest('.mmo-product__thumbs, .mmo-product__arrow');
        start = event.touches.length === 1 && !panning && !controls ? event.touches[0] : null;
        if (start) start = { x: start.clientX, y: start.clientY };
      },
      { passive: true },
    );
    slot.addEventListener(
      'touchend',
      (event) => {
        if (!start || event.touches.length) return;
        const end = event.changedTouches[0];
        const dx = end.clientX - start.x;
        const dy = end.clientY - start.y;
        start = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        step(dx < 0 ? 1 : -1);
      },
      { passive: true },
    );

    const back = (event) => {
      if (!slot.dataset.mmoView) return;
      const t = event.target;
      if (t instanceof Element && t.closest('.mmo-c') && !t.closest('.mmo-product__thumbs')) show(thumbs[0]);
    };
    document.addEventListener('input', back);
    document.addEventListener('change', back);
  }

  // ---- Analytics -------------------------------------------------------
  document.addEventListener('click', (event) => {
    const el = event.target instanceof Element ? event.target.closest('[data-mmo-event]') : null;
    if (!el) return;
    const payload = {};
    if (el.dataset.mmoLocation) payload.location = el.dataset.mmoLocation;
    try {
      window.Shopify?.analytics?.publish?.(el.dataset.mmoEvent, payload);
    } catch {
      // Analytics must never get in the way of the click.
    }
  });

  const STEP_NUMBERS = { upload: 1, route: 1, details: 2, design: 3, size: 4 };
  const stepsTouched = new Set();
  const touchStep = (event) => {
    const t = event.target instanceof Element ? event.target : null;
    const root = t && t.closest('[data-mmo-customiser]');
    if (!root || (event.type === 'click' && !t.closest('button, a'))) return;
    const n = STEP_NUMBERS[t.closest('[data-step]')?.dataset.step];
    if (!n || stepsTouched.has(n)) return;
    stepsTouched.add(n);
    try {
      window.Shopify?.analytics?.publish?.('customiser_step', { product: root.dataset.product, step: n });
    } catch {
      // Analytics must never get in the way.
    }
  };
  for (const type of ['input', 'change', 'click']) document.addEventListener(type, touchStep);

  // The design step's "Fine-tune your print" box: opened at all? Once a visit.
  let moreOpened = false;
  document.addEventListener(
    'toggle',
    (event) => {
      const box = event.target;
      if (moreOpened || !(box instanceof HTMLDetailsElement) || !box.open || !box.matches('.mmo-c__more')) return;
      moreOpened = true;
      try {
        window.Shopify?.analytics?.publish?.('customiser_options_opened', {
          product: box.closest('[data-mmo-customiser]')?.dataset.product,
          location: 'fine_tune',
        });
      } catch {
        // Analytics must never get in the way.
      }
    },
    true,
  );
})();
